import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, usePropertyAddressUrl, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  multihubLogoMonoWhiteUrl,
  repliWhiteLogoUrl,
  FacebookIcon,
  InstagramIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';

// ============================================================================
// Types & Helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

// ============================================================================
// Luminance helpers — choose mono logo variant based on secondary bg contrast
// ============================================================================

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '');
  if (clean.length !== 3 && clean.length !== 6) return null;
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

/**
 * Calculate WCAG relative luminance of a color.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine the best contrasting text color (white or dark) for a given
 * background color. Uses WCAG luminance threshold.
 */
function getContrastTextColor(bgHex: string): string {
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? '#1a1a1a' : '#ffffff';
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function formatHoursValue(openRaw: string | null | undefined, closeRaw: string | null | undefined): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!hasValue(open) && !hasValue(close)) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!hasValue(open) || !hasValue(close)) return 'Closed';
  return `${formatTime(open)} - ${formatTime(close)}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHours { label: string; hours: string; }

function normalizeDay(day: string): string { return day.trim().toLowerCase().slice(0, 3); }

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];
  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    const dayLabel = DAY_LABELS[key] || entry.day;
    const hoursStr = formatHoursValue(entry.open_time, entry.close_time);

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }
  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. Editor overrides still win.
    url: { label: 'Directions URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // No defaultField — we compute mono_white vs mono_black at runtime via luminance
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_secondary_logo', 'property_primary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_white_logo', suggestedFields: ['property_company_mono_white_logo', 'property_company_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://multihub.io' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Agency' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://repli360.com' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phoneToken = useContentToken('footer.phone');
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;
  const managedByText = useContentToken('footer.managed_by_text');
  const poweredByText = useContentToken('footer.powered_by_text');
  const designByText = useContentToken('footer.design_by_text');
  const copyrightName = useContentToken('footer.copyright_name');
  const contactHeading = useContentToken('footer.contact_heading');
  const officeHoursHeading = useContentToken('footer.office_hours_heading');
  const officeHoursEmptyText = useContentToken('footer.office_hours_empty_text');
  const accessibilityLinkLabel = useContentToken('footer.accessibility_link_label');
  const privacyPolicyLinkLabel = useContentToken('footer.privacy_policy_link_label');
  const sitemapLinkLabel = useContentToken('footer.sitemap_link_label');
  const copyrightSuffix = useContentToken('footer.copyright_suffix');

  // Resolves through Google place URL → Google business URL →
  // property_full_address → lat/lon at the runtime layer.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Dynamic copyright year — always reflects the current year
  const currentYear = new Date().getFullYear();

  // Luminance-based logo variant — read secondary color from CSS custom property
  // after mount so SSR is safe (defaults to mono_white until hydrated).
  const [logoVariant, setLogoVariant] = React.useState<'mono_white' | 'mono_black'>('mono_white');
  React.useEffect(() => {
    const secondaryHex = getComputedStyle(document.documentElement)
      .getPropertyValue('--vibe-color-secondary')
      .trim();
    const contrast = getContrastTextColor(secondaryHex);
    setLogoVariant(contrast === '#1a1a1a' ? 'mono_black' : 'mono_white');
  }, []);

  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackLogoUrl = usePropertyField('property_mono_black_logo') as string | null;
  // Pick the luminance-appropriate default; any explicit slot override wins.
  const computedDefaultLogoUrl = logoVariant === 'mono_white' ? monoWhiteLogoUrl : monoBlackLogoUrl;

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  const address = useSlotGroup('footer.address', addressSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  const hasSocial = !!(facebookLink.url || instagramLink.url);

  return (
    <footer className="footer-main">
      {/* ── Primary band — brand-colored with logo, nav, contact, hours ── */}
      <div className="footer-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] lg:grid-cols-3 gap-10 md:gap-6 lg:gap-8 items-start">

            {/* Col 1: Logo + tagline + social */}
            <div className="text-center md:text-left">
              <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
                <a href="/" className="inline-block mb-5">
                  {(logo.image || computedDefaultLogoUrl) ? (
                    <VibesitesImage url={(logo.image || computedDefaultLogoUrl)!} alt={logo.alt || 'Community Logo'} className="h-12 w-auto max-w-[180px] object-contain" />
                  ) : (
                    <div className="font-heading text-2xl footer-primary-text">Our Community</div>
                  )}
                </a>
              </EditableSlotGroup>

              <div className="footer-divider mb-5 mx-auto md:mx-0" />

              {hasSocial && (
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  {facebookLink.url && (
                    <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                      <a href={facebookLink.url} target={facebookLink._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className="footer-social-icon">
                        <FacebookIcon size={18} />
                      </a>
                    </EditableSlotGroup>
                  )}
                  {instagramLink.url && (
                    <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                      <a href={instagramLink.url} target={instagramLink._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className="footer-social-icon">
                        <InstagramIcon size={18} />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              )}
            </div>

            {/* Col 2: Contact */}
            <div className="text-center md:text-left">
              <EditableToken path="footer.contact_heading" defaultValue="Contact" as="p" className="footer-col-label">
                {contactHeading}
              </EditableToken>

              {phone && (
                <div className="mb-4">
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref footer-link inline-block"
                  >
                    <EditableToken
                      path="footer.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue=""
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>
              )}

              <EditableSlotGroup groupPath="footer.address" definition={addressSlotDef}>
                <a
                  href={address.url || propertyAddressUrl || '#'}
                  target={address._target || '_blank'}
                  rel="noopener noreferrer"
                  className="footer-link block leading-relaxed"
                >
                  <span className="block">{address.address}</span>
                </a>
              </EditableSlotGroup>
            </div>

            {/* Col 3: Office Hours */}
            <div className="text-center md:text-left">
              <EditableToken path="footer.office_hours_heading" defaultValue="Office Hours" as="p" className="footer-col-label">
                {officeHoursHeading}
              </EditableToken>
              {groupedHours.length > 0 ? (
                <dl className="space-y-1.5 w-fit md:w-full mx-auto md:mx-0 text-left">
                  {groupedHours.map((group, index) => (
                    <div key={index} className="flex justify-between gap-4 lg:gap-6 font-body text-sm footer-primary-text">
                      <dt>{group.label}</dt>
                      <dd className="text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <EditableToken path="footer.office_hours_empty_text" defaultValue="Office hours will be displayed here." as="p" className="font-body text-sm footer-primary-text-muted">
                  {officeHoursEmptyText}
                </EditableToken>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom bar — darker, compliance + attribution ── */}
      <div className="footer-bottom">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Compliance icons + legal */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-5 text-white/70">
              <FairHousingIcon size={28} color="currentColor" />
              <PetsAllowedIcon size={28} color="currentColor" />
              <WheelchairIcon size={28} color="currentColor" />
            </div>
            <nav className="flex items-center gap-2 font-body text-xs text-white/70" aria-label="Legal links">
              <a href="/accessibility" className="hover:text-white transition-colors">
                <EditableToken path="footer.accessibility_link_label" defaultValue="Accessibility" as="span">
                  {accessibilityLinkLabel}
                </EditableToken>
              </a>
              <span aria-hidden="true">·</span>
              <a href="/privacy-policy" className="hover:text-white transition-colors">
                <EditableToken path="footer.privacy_policy_link_label" defaultValue="Privacy Policy" as="span">
                  {privacyPolicyLinkLabel}
                </EditableToken>
              </a>
              <span aria-hidden="true">·</span>
              <a href="/sitemap" className="hover:text-white transition-colors">
                <EditableToken path="footer.sitemap_link_label" defaultValue="Sitemap" as="span">
                  {sitemapLinkLabel}
                </EditableToken>
              </a>
            </nav>
          </div>

          {/* Attribution row */}
          <div className="footer-attribution-row flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
            {/* Managed By */}
            <div className="flex items-center gap-2">
              <EditableToken path="footer.managed_by_text" defaultValue="Professionally Managed by" as="span" className="font-body text-[10px] text-white/70">
                {managedByText}
              </EditableToken>
              <EditableSlotGroup groupPath="footer.managed_by_logo" definition={managedByLogoDef}>
                {managedByLogo.image ? (
                  <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer">
                    <VibesitesImage url={managedByLogo.image} alt={managedByLogo.alt || 'Management Company'} className="h-4 w-auto max-w-[100px] object-contain opacity-80 hover:opacity-100 transition-opacity" />
                  </a>
                ) : null}
              </EditableSlotGroup>
            </div>

            {/* Copyright — year updates dynamically, property name pulled from data */}
            <p className="font-body text-[10px] text-white/70 order-last md:order-none">
              &copy; {currentYear}{' '}
              <EditableToken path="footer.copyright_suffix" defaultValue="All Rights Reserved." as="span">
                {copyrightSuffix}
              </EditableToken>{' '}
              <EditableToken path="footer.copyright_name" defaultField="property_name" defaultValue="Property Name" as="span">
                {copyrightName}
              </EditableToken>
            </p>

            {/* Powered By + Design By */}
            <div className="flex items-center gap-5">
              <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.powered_by_text" defaultValue="Powered by" as="span" className="font-body text-[10px] text-white/70">
                    {poweredByText}
                  </EditableToken>
                  <a href={poweredByLogo.url || 'https://multihub.io'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                    <VibesitesImage url={poweredByLogo.image || multihubLogoMonoWhiteUrl} alt={poweredByLogo.alt || 'Multihub'} className="h-4 w-auto max-w-[100px] object-contain" />
                  </a>
                </div>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                <div className="flex items-center gap-1.5">
                  <EditableToken path="footer.design_by_text" defaultValue="Website Design by" as="span" className="font-body text-[10px] text-white/70">
                    {designByText}
                  </EditableToken>
                  <a href={designByLogo.url || 'https://repli360.com'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-80 hover:opacity-100 transition-opacity">
                    <VibesitesImage url={designByLogo.image || repliWhiteLogoUrl} alt={designByLogo.alt || 'Agency'} className="h-4 w-auto max-w-[100px] object-contain" />
                  </a>
                </div>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Primary footer band — uses secondary brand color */
        .footer-primary {
          background: linear-gradient(
            160deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
        }

        /* Bottom bar — deeper, darker shade */
        .footer-bottom {
          background: color-mix(in srgb, var(--vibe-color-secondary) 70%, black);
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        .footer-attribution-row {
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        /* Helper classes — always track textOnSecondary for the primary band */
        .footer-primary-text {
          color: var(--vibe-color-text-on-secondary);
        }
        .footer-primary-text-muted {
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 40%, transparent);
        }

        /* Column label — Contact / Office Hours headings */
        .footer-col-label {
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          margin-bottom: 1rem;
        }

        /* Nav/contact links */
        .footer-link {
          font-size: 0.875rem;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 70%, transparent);
          transition: color 0.2s ease;
        }
        .footer-link:hover {
          color: var(--vibe-color-text-on-secondary);
        }

        /* Accent line under logo — tracks textOnSecondary */
        .footer-divider {
          width: 40px;
          height: 2px;
          background: color-mix(in srgb, var(--vibe-color-text-on-secondary) 60%, transparent);
        }

        /* Social icon circles */
        .footer-social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: 1px solid color-mix(in srgb, var(--vibe-color-text-on-secondary) 20%, transparent);
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 60%, transparent);
          transition: all 0.25s ease;
        }
        .footer-social-icon:hover {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'text',
      defaultValue: 'Professionally Managed by',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'text',
      defaultValue: 'Powered by',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'text',
      defaultValue: 'Website Design by',
    },
    'footer.copyright_name': {
      label: 'Copyright Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Property Name',
    },
    'footer.contact_heading': {
      label: 'Contact Column Heading',
      type: 'text',
      defaultValue: 'Contact',
    },
    'footer.office_hours_heading': {
      label: 'Office Hours Column Heading',
      type: 'text',
      defaultValue: 'Office Hours',
    },
    'footer.office_hours_empty_text': {
      label: 'Office Hours Empty State Text',
      type: 'text',
      defaultValue: 'Office hours will be displayed here.',
    },
    'footer.accessibility_link_label': {
      label: 'Accessibility Link Label',
      type: 'text',
      defaultValue: 'Accessibility',
    },
    'footer.privacy_policy_link_label': {
      label: 'Privacy Policy Link Label',
      type: 'text',
      defaultValue: 'Privacy Policy',
    },
    'footer.sitemap_link_label': {
      label: 'Sitemap Link Label',
      type: 'text',
      defaultValue: 'Sitemap',
    },
    'footer.copyright_suffix': {
      label: 'Copyright Suffix',
      type: 'text',
      defaultValue: 'All Rights Reserved.',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.address': addressSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
